import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  CoachFinderSchema,
  isUnrestrictedCoachingFormat,
} from "~/lib/schemas/coach-discovery";
import {
  DEFAULT_MATCHING_WEIGHTS,
  type SimulatedMatchScore,
} from "~/lib/schemas/coach-matching-debug";

const SimulateMatchScoreInputSchema = z.object({
  coachId: z.string().uuid(),
  preferences: CoachFinderSchema,
});

/**
 * Simulates a match score for a specific coach given user preferences.
 * Returns detailed breakdown of all scoring components. The hard-filter
 * results reveal private coach attributes, so admins only.
 */
export const simulateCoachMatchScore = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:read")])
  .validator((data: unknown) => SimulateMatchScoreInputSchema.parse(data))
  .handler(async ({ data }): Promise<SimulatedMatchScore | null> => {
    const { coachId, preferences } = data;

    const { findCoachMatches } = await import("~/lib/server/coach-matching");

    // Hard requirements off, so the coach is scored even when a hard filter
    // would normally exclude them
    const [match] = await findCoachMatches({
      preferences,
      limit: 1,
      coachIds: [coachId],
      options: {
        enforceHardRequirements: false,
      },
    });

    if (!match) {
      return null;
    }

    const scores = {
      specialty: match.detailedScores.attributeComponents.specialty,
      vectorSimilarity: match.detailedScores.vectorSimilarity,
      experienceLevel: match.detailedScores.attributeComponents.experienceLevel,
      location: match.detailedScores.attributeComponents.location,
      age: match.detailedScores.attributeComponents.age,
      coachingApproach:
        match.detailedScores.attributeComponents.coachingApproach,
      personality: match.detailedScores.attributeComponents.personality,
      status: match.detailedScores.attributeComponents.status,
      sessionFormat: match.detailedScores.attributeComponents.sessionFormat,
      gender: match.detailedScores.attributeComponents.gender,
      faith: match.detailedScores.attributeComponents.faith,
    };

    const weights = DEFAULT_MATCHING_WEIGHTS;

    // Calculate hard filter results based on preferences and coach data
    const hardFilters = {
      location:
        preferences.prefersCoachLocation !== "Yes" ||
        !preferences.coachLocationCountry ||
        (match.profile?.primaryCountry ?? "") ===
          preferences.coachLocationCountry,
      language:
        preferences.preferredLanguages.length === 0 ||
        preferences.preferredLanguages.some((lang: string) =>
          (match.profile?.languages ?? "").includes(lang),
        ),
      sessionDelivery:
        !preferences.sessionDeliveryMethods ||
        preferences.sessionDeliveryMethods === "No preference" ||
        (match.details?.sessionDeliveryMethods ?? []).includes(
          preferences.sessionDeliveryMethods,
        ),
      coachingFormat:
        isUnrestrictedCoachingFormat(preferences.coachingFormats) ||
        (match.details?.coachingFormats ?? []).includes(
          preferences.coachingFormats,
        ),
      faith:
        preferences.faithIntegration.length === 0 ||
        preferences.faithIntegration.includes("No preference") ||
        preferences.faithIntegration.some((f: string) =>
          (match.details?.faithIntegration ?? []).includes(f),
        ),
      gender:
        !preferences.coachGender ||
        preferences.coachGender === "No preference" ||
        match.details?.gender === preferences.coachGender,
    };

    // Calculate total weighted score
    const totalWeighted =
      scores.specialty * weights.specialty +
      scores.vectorSimilarity * weights.vectorSimilarity +
      scores.experienceLevel * weights.experienceLevel +
      scores.location * weights.location +
      scores.age * weights.age +
      scores.coachingApproach * weights.coachingApproach +
      scores.personality * weights.personality +
      scores.status * weights.status +
      scores.sessionFormat * weights.sessionFormat +
      scores.gender * weights.gender +
      scores.faith * weights.faith;

    const weightSum =
      weights.specialty +
      weights.vectorSimilarity +
      weights.experienceLevel +
      weights.location +
      weights.age +
      weights.coachingApproach +
      weights.personality +
      weights.status +
      weights.sessionFormat +
      weights.gender +
      weights.faith;

    const normalizedScore = totalWeighted / weightSum;

    return {
      scores,
      weights,
      hardFilters,
      totalWeighted,
      normalizedScore,
    };
  });
